import { FocusEvent, FocusEventHandler, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useController,
  UseControllerProps,
  useFormContext,
} from 'react-hook-form';
import { convertDateToISOString } from '../../../helpers/dates';
import { transformValidationOptions } from '../../../helpers/shared/transformValidationOptions';
import { applyDateValidationOptions } from './applyDateValidationOptions';

export interface UseDateFieldControllerProps<TFieldValues>
  extends UseControllerProps<TFieldValues> {
  ISOFormat?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: (value: string | null) => void;
}

export function useDateFieldController<TFieldValues>(
  props: UseDateFieldControllerProps<TFieldValues>,
) {
  const {
    name,
    rules,
    onChange,
    onBlur,
    ISOFormat,
    ...dateFieldControllerProps
  } = props;

  const { t } = useTranslation();
  const { control, getValues } = useFormContext<TFieldValues>();
  const transformedRules = transformValidationOptions<TFieldValues>(
    t,
    rules,
    getValues,
  );
  const dateFieldRules = applyDateValidationOptions(t, transformedRules);
  const required = typeof dateFieldRules.required !== 'undefined';
  const controllerProps = useController<TFieldValues>({
    name,
    control,
    rules: dateFieldRules,
    ...dateFieldControllerProps,
  });
  const { field } = controllerProps;

  const handleChange = useCallback(
    (value: unknown) => {
      const date = value as Date | null;

      if (!date) {
        field.onChange(null);

        if (onChange) {
          onChange(null);
        }
        return;
      }

      const transformedDate = convertDateToISOString(date, ISOFormat);
      field.onChange(transformedDate);

      if (onChange) {
        onChange(transformedDate);
      }
    },
    [onChange, ISOFormat, field],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      field.onBlur();

      if (onBlur) {
        onBlur(event);
      }
    },
    [field, onBlur],
  );

  return {
    required,
    onChangeField: handleChange,
    onBlurField: handleBlur,
    ...controllerProps,
  };
}
